import React, { useEffect, useState } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { FileText, Search, Download, Check, X, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/utils/api';
import { showSuccess, showError } from '@/utils/toast';

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const leadsRes = await api.get('/leads');
      setLeads(leadsRes.data);

      // Fetch documents for all leads in parallel
      const allDocs = [];
      await Promise.all(
        leadsRes.data.map(async (lead) => {
          try {
            const docsRes = await api.get(`/leads/${lead.id}/documents`);
            docsRes.data.forEach((d) => allDocs.push({ ...d, lead_name: lead.customer_name, lead_mobile: lead.mobile_number }));
          } catch (e) {
            // Non-fatal: user may not have access to a specific lead's docs; keep aggregating others
            console.warn(`[Documents] skip lead ${lead.id}:`, e?.response?.status || e?.message);
          }
        })
      );
      setDocuments(allDocs);
    } catch (error) {
      showError('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (documentId, status) => {
    try {
      await api.put(`/documents/${documentId}/status?status=${status}`);
      showSuccess(`Document marked as ${status}`);
      fetchData();
    } catch (error) {
      showError('Failed to update document status');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      showError('Failed to download document');
    }
  };

  const filtered = documents.filter((d) => {
    const matchSearch = d.lead_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       d.original_filename?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = !filterStatus || d.status === filterStatus;
    const matchType = !filterType || d.document_type === filterType;
    return matchSearch && matchStatus && matchType;
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-primary" size={40} />
        </div>
      </DashboardLayout>
    );
  }

  const getStatusColor = (status) => {
    const map = {
      uploaded: 'bg-blue-100 text-blue-800',
      verified: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      pending: 'bg-gray-100 text-gray-800',
    };
    return map[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <DashboardLayout>
      <div>
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Documents</h1>
          <p className="text-muted-foreground">All uploaded customer documents</p>
        </div>

        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
              <input
                type="text"
                data-testid="search-documents"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none"
              />
            </div>
            <select
              data-testid="filter-doc-type"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none"
            >
              <option value="">All Document Types</option>
              <option value="aadhaar">Aadhaar</option>
              <option value="pan">PAN</option>
              <option value="bank_statement">Bank Statement</option>
              <option value="salary_slip">Salary Slip</option>
              <option value="itr">ITR</option>
              <option value="gst_certificate">GST</option>
              <option value="property_papers">Property Papers</option>
            </select>
            <select
              data-testid="filter-doc-status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none"
            >
              <option value="">All Status</option>
              <option value="uploaded">Uploaded</option>
              <option value="verified">Verified</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted border-b border-border">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase">Document</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase">Customer</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase">Type</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-muted-foreground">No documents found</td>
                  </tr>
                ) : (
                  filtered.map((doc) => (
                    <tr key={doc.id} data-testid={`doc-row-${doc.id}`} className="hover:bg-accent/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <FileText className="text-primary" size={20} />
                          <span className="text-sm text-foreground truncate max-w-xs">{doc.original_filename}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Link to={`/leads/${doc.lead_id}`} className="text-sm text-foreground hover:text-primary">{doc.lead_name}</Link>
                        <p className="text-xs text-muted-foreground">{doc.lead_mobile}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-foreground capitalize">{doc.document_type.replace('_', ' ')}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(doc.status)}`}>
                          {doc.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          <button onClick={() => handleDownload(doc)} className="p-1.5 rounded hover:bg-muted" title="Download">
                            <Download size={16} className="text-muted-foreground" />
                          </button>
                          <button onClick={() => handleUpdateStatus(doc.id, 'verified')} className="p-1.5 rounded hover:bg-green-50" title="Verify">
                            <Check size={16} className="text-green-600" />
                          </button>
                          <button onClick={() => handleUpdateStatus(doc.id, 'rejected')} className="p-1.5 rounded hover:bg-red-50" title="Reject">
                            <X size={16} className="text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Documents;
